/* eslint-disable indent */
/* eslint-disable react/prop-types */
import React from 'react';
import PropTypes from 'prop-types';
import { get, keyBy, cloneDeep, concat, orderBy } from 'lodash';
import moment from 'moment';
import { injectIntl, FormattedMessage } from 'react-intl';

import {
  AppIcon,
  CalloutContext,
  IfPermission,
  IfInterface,
  TitleManager,
  withOkapiKy,
} from '@folio/stripes/core';
import {
  Pane,
  PaneMenu,
  IconButton,
  expandAllFunction,
  ExpandAllButton,
  Callout,
  Row,
  Col,
  Headline,
  AccordionSet,
  LoadingPane,
  HasCommand,
} from '@folio/stripes/components';
import {
  NotesSmartAccordion,
  ViewCustomFieldsRecord,
  NotePopupModal,
} from '@folio/stripes/smart-components';

import {
  UserInfo,
  ExtendedInfo,
  ContactInfo,
  ProxyPermissions,
  PatronBlock,
  UserPermissions,
  UserLoans,
  UserRequests,
  UserAccounts,
  UserAffiliations,
  UserServicePoints,
} from '../../components/UserDetailSections';
import HelperApp from '../../components/HelperApp';
import IfConsortium from '../../components/IfConsortium';
import { PatronBlockMessage } from '../../components/PatronBlock';
import { getFormAddressList } from '../../components/data/converters/address';
import {
  getFullName,
  isAffiliationsEnabled,
  isDcbUser,
} from '../../components/util';
import RequestFeeFineBlockButtons from '../../components/RequestFeeFineBlockButtons';
import { departmentsShape } from '../../shapes';
import ErrorPane from '../../components/ErrorPane';
import LostItemsLink from '../../components/LostItemsLink';
import IfConsortiumPermission from '../../components/IfConsortiumPermission';
import { USER_TYPES } from '../../constants';
import ActionMenuEditButton from './components/ActionMenuEditButton';
import ActionMenuDeleteButton from './components/ActionMenuDeleteButton';
import OpenTransactionModal from './components/OpenTransactionModal';
import DeleteUserModal from './components/DeleteUserModal';
import ExportFeesFinesReportButton from './components';
/** kware start edit - Import ActionMenuChangePasswordButton component for staff password change */
import { ActionMenuChangePasswordButton } from './components';
/** kware end edit */
/** kware start edit - Import user clearance components */
import ChangePasswordModal from '../../components/EditSections/EditExtendedInfo/CreateResetPasswordControl/Modals/ChangePasswordModal';
/** kware end edit - Import change password modal for staff users */
/** kware start edit - Import user clearance components */
import {
  ClearanceModal,
  ActionMenuClearanceButton,
  ClearanceBlockedModal,
  ClearanceBanner,
  ClearanceInfo,
  DefaultLibraryInfo,
  OpenTransactionsReportModal,
  ActionMenuOpenTransactionsButton,
  exportOpenTransactionsToExcel,
  exportOpenTransactionsToPdf,
} from '../../components/UserClearance';
/** kware end edit */

class UserDetail extends React.Component {
  static propTypes = {
    stripes: PropTypes.shape({
      hasInterface: PropTypes.func.isRequired,
      hasPerm: PropTypes.func.isRequired,
      connect: PropTypes.func.isRequired,
      locale: PropTypes.string.isRequired,
      logger: PropTypes.shape({
        log: PropTypes.func.isRequired,
      }).isRequired,
    }).isRequired,
    mutator: PropTypes.shape({
      hasManualPatronBlocks: PropTypes.shape({
        GET: PropTypes.func,
      }),
      hasAutomatedPatronBlocks: PropTypes.shape({
        GET: PropTypes.func,
      }),
      delUser: PropTypes.shape({
        DELETE: PropTypes.func,
      }),
      openTransactions: PropTypes.shape({
        GET: PropTypes.func,
      }),
      currentUserData: PropTypes.shape({
        GET: PropTypes.func,
      }),
    }),
    resources: PropTypes.shape({
      selUser: PropTypes.object,
      delUser: PropTypes.object,
      openTransactions: PropTypes.object,
      user: PropTypes.arrayOf(PropTypes.object),
      accounts: PropTypes.shape({
        records: PropTypes.arrayOf(PropTypes.object),
      }),
      addressTypes: PropTypes.shape({
        records: PropTypes.arrayOf(PropTypes.object),
      }),
      departments: PropTypes.shape({
        records: departmentsShape,
      }),
      permissions: PropTypes.shape({
        records: PropTypes.arrayOf(PropTypes.object),
      }),
      // query: PropTypes.object,
      patronGroups: PropTypes.shape({
        records: PropTypes.arrayOf(PropTypes.object),
      }),
      settings: PropTypes.shape({
        records: PropTypes.arrayOf(PropTypes.object),
      }),
      loansHistory: PropTypes.shape({
        records: PropTypes.arrayOf(PropTypes.object),
      }),
      suppressEdit: PropTypes.shape({
        records: PropTypes.arrayOf(PropTypes.object),
      }),
    }),
    match: PropTypes.shape({
      path: PropTypes.string.isRequired,
      params: PropTypes.shape({
        id: PropTypes.string,
      }),
    }).isRequired,
    okapi: PropTypes.shape({
      currentUser: PropTypes.shape({
        servicePoints: PropTypes.arrayOf(PropTypes.object),
      }).isRequired,
    }).isRequired,
    okapiKy: PropTypes.func.isRequired,
    onClose: PropTypes.func,
    tagsToggle: PropTypes.func,
    location: PropTypes.object,
    history: PropTypes.object,
    getSponsors: PropTypes.func,
    getProxies: PropTypes.func,
    getUserServicePoints: PropTypes.func,
    getPreferredServicePoint: PropTypes.func,
    tagsEnabled: PropTypes.bool,
    paneWidth: PropTypes.string,
    intl: PropTypes.object.isRequired,
  };

  static defaultProps = {
    paneWidth: '44%',
  };

  static contextType = CalloutContext;

  constructor(props) {
    super(props);

    this.editButton = React.createRef();

    this.keyboardCommands = [
      {
        name: 'edit',
        handler: this.goToEdit,
      },
      {
        name: 'collapseAllSections',
        handler: this.collapseAllSections,
      },
      {
        name: 'expandAllSections',
        handler: this.expandAllSections,
      },
    ];

    this.state = {
      patronBlocks: [],
      lastUpdate: null,
      showOpenTransactionModal: false,
      showDeleteUserModal: false,
      /** kware start edit - Add clearance modal state */
      showClearanceModal: false,
      clearanceLoading: false,
      showClearanceBlockedModal: false,
      showUndoClearanceModal: false,
      clearanceByUserName: null,
      showOpenTransactionsReportModal: false,
      openTransactionsReportInProgress: false,
      showChangePasswordModal: false,
      changePasswordLoading: false,
      /** kware end edit */
      sections: {
        userInformationSection: true,
        affiliationsSection: false,
        extendedInfoSection: false,
        contactInfoSection: false,
        proxySection: false,
        patronBlocksSection: false,
        loansSection: false,
        requestsSection: false,
        accountsSection: false,
        permissionsSection: false,
        servicePointsSection: false,
        notesAccordion: false,
        customFields: false,
        clearanceSection: false,
        defaultLibrarySection: false,
      },
    };

    this.callout = null;
  }

  componentDidMount() {
    this._isMounted = true;
    this.loadPatronBlocks();
    this.loadClearanceByUser();
  }

  componentDidUpdate(prevProps) {
    const user = this.getUser();
    const prevUser = prevProps.resources?.selUser?.records?.[0];
    const clearanceBy = user?.customFields?.clearanceby;
    const prevClearanceBy = prevUser?.customFields?.clearanceby;

    if (clearanceBy && clearanceBy !== prevClearanceBy) {
      this.loadClearanceByUser();
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  getUser = () => {
    const {
      resources,
      match: {
        params: { id },
      },
    } = this.props;
    const selUser = (resources.selUser || {}).records || [];

    if (selUser.length === 0 || !id) return null;
    // Logging below shows this DOES sometimes find the wrong record. But why?
    // console.log(`getUser: found ${selUser.length} users, id '${selUser[0].id}' ${selUser[0].id === id ? '==' : '!='} '${id}'`);
    return selUser.find((u) => u.id === id);
  };

  /** kware start edit - Add function to get current user with custom fields */
  getCurrentUser = () => {
    const { resources, okapi } = this.props;
    const currentUserData = (resources.currentUserData || {}).records || [];
    const currentUserId = okapi.currentUser.id;

    if (currentUserData.length === 0 || !currentUserId) return null;
    return currentUserData.find((u) => u.id === currentUserId);
  };
  /** kware end edit */

  handleDeleteUser = (id) => {
    const { history, location, mutator, intl } = this.props;
    const user = this.getUser();
    const fullNameOfUser = getFullName(user, intl);

    try {
      mutator.delUser
        .DELETE({ id })
        .then(() =>
          history.replace({
            pathname: '/users',
            search: `${location.search}`,
            state: { deletedUserId: id },
          }),
        )
        .then(() => {
          this.context.sendCallout({
            type: 'success',
            message: intl.formatMessage(
              { id: 'ui-users.details.deleteUser.success' },
              { name: fullNameOfUser },
            ),
          });
        });
    } catch (error) {
      this.context.sendCallout({
        type: 'error',
        message: intl.formatMessage(
          { id: 'ui-users.details.deleteUser.failed' },
          { name: fullNameOfUser },
        ),
      });
    }
  };

  checkScope = () => {
    return document
      .getElementById('ModuleContainer')
      .contains(document.activeElement);
  };

  // This is a helper function for the "last updated" date element. Since the
  // date/time is actually set on the server when the record is updated, the
  // lastUpdated element of the record on the client side might contain a stale
  // value. If so, this returns a locally stored update date until the data
  // is refreshed.
  dateLastUpdated(user) {
    const updatedDateRec = get(user, ['updatedDate'], '');
    const updatedDateLocal = this.state.lastUpdate;

    if (!updatedDateRec) {
      return '';
    }

    let dateToShow;
    if (updatedDateLocal && updatedDateLocal > updatedDateRec) {
      dateToShow = updatedDateLocal;
    } else {
      dateToShow = updatedDateRec;
    }

    return new Date(dateToShow).toLocaleString(this.props.stripes.locale);
  }

  showHelperApp = (helperName) => {
    this.setState({
      helperApp: helperName,
    });
  };

  closeHelperApp = () => {
    this.setState({
      helperApp: null,
    });
  };

  handleExpandAll = (obj) => {
    this.setState((curState) => {
      const newState = cloneDeep(curState);
      newState.sections = obj;
      return newState;
    });
  };

  handleSectionToggle = ({ id }) => {
    this.setState((curState) => {
      const newState = cloneDeep(curState);
      newState.sections[id] = !newState.sections[id];
      return newState;
    });
  };

  toggleAllSections = (expand) => {
    this.setState((curState) => {
      const newSections = expandAllFunction(curState.sections, expand);
      return {
        sections: newSections,
      };
    });
  };

  expandAllSections = (e) => {
    e.preventDefault();
    this.toggleAllSections(true);
  };

  collapseAllSections = (e) => {
    e.preventDefault();
    this.toggleAllSections(false);
  };

  // focus management when edit layer closes (refocus edit button)
  afterCloseEdit = () => {
    if (this.editButton.current) {
      this.editButton.current.focus();
    }
  };

  getEditLink = () => {
    const {
      match: { params },
      location: { search },
    } = this.props;

    return `/users/${params.id}/edit${search}`;
  };

  onClose = () => {
    const { history, location } = this.props;

    history.push(`/users${location.search}`);
  };

  getPatronGroup(user) {
    const { resources } = this.props;
    const patronGroups = (resources.patronGroups || {}).records || [];
    const patronGroupId = get(user, ['patronGroup'], '');
    return patronGroups.find((g) => g.id === patronGroupId) || { group: '' };
  }

  /** kware start edit - Add function to get library name */
  getLibraryName = (libraryId) => {
    const { resources, intl } = this.props;
    const libraries = (resources.libraries || {}).records || [];

    if (!libraryId) return 'Unknown Library';

    const library = libraries.find((lib) => lib.id === libraryId);
    if (!library) return `Library ID: ${libraryId}`;

    const translationKey = `ui-tenant-settings.loclibs.name.${library.name}`;
    return intl.formatMessage(
      { id: translationKey },
      { defaultMessage: library.name },
    );
  };

  loadClearanceByUser = async () => {
    const user = this.getUser();
    const clearanceBy = user?.customFields?.clearanceby;

    if (!clearanceBy || this.state.clearanceByUserName) return;

    try {
      const response = await fetch(
        `${this.props.stripes.okapi.url}/users/${clearanceBy}`,
        {
          headers: {
            'X-Okapi-Tenant': this.props.stripes.okapi.tenant,
            'X-Okapi-Token': this.props.stripes.okapi.token,
            'Content-Type': 'application/json',
          },
        },
      );

      if (response.ok) {
        const userData = await response.json();
        const fullName = getFullName(userData, this.props.intl);
        if (this._isMounted) {
          this.setState({ clearanceByUserName: fullName });
        }
      }
    } catch (error) {
      // Silently fail
    }
  };

  getUserNameById = () => {
    return this.state.clearanceByUserName || '-';
  };
  /** kware end edit */

  renderDetailsLastMenu(user) {
    const { tagsEnabled, intl } = this.props;

    const tags = ((user && user.tags) || {}).tagList || [];

    return (
      <PaneMenu>
        {tagsEnabled && (
          <IconButton
            icon="tag"
            id="clickable-show-tags"
            onClick={() => {
              this.showHelperApp('tags');
            }}
            badgeCount={tags.length}
            aria-label={intl.formatMessage({ id: 'ui-users.showTags' })}
          />
        )}
      </PaneMenu>
    );
  }

  showOpenTransactionsModal(json) {
    this.setState({
      showOpenTransactionModal: true,
      openTransactions: json,
    });
  }

  showDeleteUserModal(json) {
    this.setState({
      showDeleteUserModal: true,
      openTransactions: json,
    });
  }

  doCloseTransactionDeleteModal = () => {
    this.setState({
      showDeleteUserModal: false,
      showOpenTransactionModal: false,
    });
  };

  selectModal(transactions) {
    if (!transactions?.hasOpenTransactions) {
      this.showDeleteUserModal();
    } else {
      this.showOpenTransactionsModal(transactions);
    }
  }

  handleDeleteClick = () => {
    const { mutator } = this.props;
    const userId = this.props.match.params.id;

    mutator.openTransactions.GET({ userId }).then((response) => {
      this.selectModal(response);
    });
  };

  /** kware start edit - Add user clearance functions */
  handleClearanceClick = () => {
    const { mutator } = this.props;
    const userId = this.props.match.params.id;

    mutator.openTransactions.GET({ userId }).then((response) => {
      if (response.hasOpenTransactions) {
        this.setState({
          showClearanceBlockedModal: true,
          openTransactions: response,
        });
      } else {
        this.setState({ showClearanceModal: true });
      }
    });
  };

  handleCloseClearanceBlockedModal = () => {
    this.setState({ showClearanceBlockedModal: false });
  };

  handleClearanceConfirm = async (libraryId, clearanceDate) => {
    const { mutator, intl, okapi } = this.props;
    const user = this.getUser();
    const fullNameOfUser = getFullName(user, intl);

    this.setState({ clearanceLoading: true });

    try {
      const formattedDate = clearanceDate.toISOString().split('T')[0];

      const updatedUser = {
        ...user,
        active: false,
        expirationDate: formattedDate,
        customFields: {
          ...user.customFields,
          clearancestatus: 'opt_0',
          clearancedate: formattedDate,
          clearancelibrary: libraryId,
          clearanceby: okapi.currentUser.id,
        },
      };

      await mutator.selUser.PUT(updatedUser);

      this.setState({
        showClearanceModal: false,
        clearanceLoading: false,
      });

      this.context.sendCallout({
        type: 'success',
        message: intl.formatMessage(
          { id: 'ui-users.clearance.success' },
          { name: fullNameOfUser },
        ),
      });
    } catch (error) {
      this.setState({ clearanceLoading: false });
      this.context.sendCallout({
        type: 'error',
        message: intl.formatMessage(
          { id: 'ui-users.clearance.error' },
          { name: fullNameOfUser },
        ),
      });
    }
  };

  handleCloseClearanceModal = () => {
    this.setState({ showClearanceModal: false });
  };

  handleUndoClearanceClick = () => {
    this.setState({ showUndoClearanceModal: true });
  };

  handleUndoClearanceConfirm = async () => {
    const { mutator, intl } = this.props;
    const user = this.getUser();
    const fullNameOfUser = getFullName(user, intl);

    this.setState({ clearanceLoading: true });

    try {
      const updatedUser = {
        ...user,
        active: true,
        expirationDate: null,
        customFields: {
          ...user.customFields,
          clearancestatus: 'opt_1',
          clearancedate: '',
          clearancelibrary: '',
          clearanceby: '',
        },
      };

      await mutator.selUser.PUT(updatedUser);

      this.setState({
        showUndoClearanceModal: false,
        clearanceLoading: false,
      });

      this.context.sendCallout({
        type: 'success',
        message: intl.formatMessage(
          { id: 'ui-users.clearance.undo.success' },
          { name: fullNameOfUser },
        ),
      });
    } catch (error) {
      this.setState({ clearanceLoading: false });
      this.context.sendCallout({
        type: 'error',
        message: intl.formatMessage(
          { id: 'ui-users.clearance.undo.error' },
          { name: fullNameOfUser },
        ),
      });
    }
  };

  handleCloseUndoClearanceModal = () => {
    this.setState({ showUndoClearanceModal: false });
  };

  handleOpenTransactionsReportClick = () => {
    this.setState({ showOpenTransactionsReportModal: true });
  };

  handleCloseOpenTransactionsReportModal = () => {
    this.setState({ showOpenTransactionsReportModal: false });
  };

  /** kware start edit - Add change password functions for staff users */
  handleChangePasswordClick = () => {
    this.setState({ showChangePasswordModal: true });
  };

  handleCloseChangePasswordModal = () => {
    this.setState({ showChangePasswordModal: false });
  };

  handleChangePasswordSubmit = async (newPassword) => {
    const { intl, okapiKy } = this.props;
    const user = this.getUser();
    const fullNameOfUser = getFullName(user, intl);

    /* kware start edit - Handle password change submission
     * Step 1: Delete old credentials using DELETE /authn/credentials?userId={userId}
     * Step 2: Create new credentials using POST /authn/credentials
     * This approach is needed because PUT is not supported for updating passwords.
     */
    this.setState({ changePasswordLoading: true });

    try {
      // Step 1: Delete old credentials
      await okapiKy.delete(`authn/credentials?userId=${user.id}`);

      // Step 2: Create new credentials with new password
      await okapiKy.post('authn/credentials', {
        json: {
          userId: user.id,
          password: newPassword,
        },
      });

      this.setState({
        showChangePasswordModal: false,
        changePasswordLoading: false,
      });

      this.context.sendCallout({
        type: 'success',
        message: intl.formatMessage(
          { id: 'ui-users.extended.changePasswordModal.success' },
          { name: fullNameOfUser },
        ),
      });
    } catch (error) {
      this.setState({ changePasswordLoading: false });
      this.context.sendCallout({
        type: 'error',
        message: intl.formatMessage(
          { id: 'ui-users.extended.changePasswordModal.error' },
          { name: fullNameOfUser },
        ),
      });
    }
  };
  /** kware end edit */

  handleOpenTransactionsReportExport = async (reportName, format) => {
    const { mutator, intl, stripes } = this.props;
    const user = this.getUser();
    const fullNameOfUser = getFullName(user, intl);

    this.setState({
      openTransactionsReportInProgress: true,
      showOpenTransactionsReportModal: false,
    });

    try {
      this.context.sendCallout({
        message: <FormattedMessage id="ui-users.reports.inProgress" />,
      });

      const userId = this.props.match.params.id;
      const openTransactions = await mutator.openTransactions.GET({ userId });

      const patronGroups =
        (this.props.resources.patronGroups || {}).records || [];
      const patronGroup = patronGroups.find((g) => g.id === user.patronGroup);

      // Fetch detailed loan data
      const loanRecords =
        (this.props.resources.loanRecords || {}).records || [];
      const accounts = (this.props.resources.accounts || {}).records || [];

      const data = {
        userName: fullNameOfUser,
        barcode: user.barcode || '-',
        patronGroup: patronGroup
          ? intl.formatMessage(
              { id: `ui-users.patronGroups.group.${patronGroup.group}` },
              { defaultMessage: patronGroup.group },
            )
          : '-',
        openLoansCount: openTransactions.loans || 0,
        openRequestsCount: openTransactions.requests || 0,
        outstandingFeesCount: openTransactions.feesFines || 0,
        totalFeeAmount: accounts.reduce(
          (sum, acc) => sum + (acc.remaining || 0),
          0,
        ),
        openLoans: loanRecords
          .filter((loan) => loan.status?.name === 'Open')
          .map((loan) => {
            const today = new Date();
            const dueDate = loan.dueDate ? new Date(loan.dueDate) : null;
            const overdueDays =
              dueDate && dueDate < today
                ? Math.floor((today - dueDate) / (1000 * 60 * 60 * 24))
                : 0;

            return {
              itemTitle: loan.item?.title || '-',
              itemBarcode: loan.item?.barcode || '-',
              loanDate: loan.loanDate
                ? new Date(loan.loanDate).toLocaleDateString('ar-SA')
                : '-',
              dueDate: loan.dueDate
                ? new Date(loan.dueDate).toLocaleDateString('ar-SA')
                : '-',
              overdueDays,
              status:
                overdueDays > 0
                  ? intl.formatMessage({
                      id: 'ui-users.loans.columns.itemStatus',
                    })
                  : intl.formatMessage({ id: 'ui-users.active' }),
            };
          }),
        openRequests: [],
        outstandingFees: accounts
          .filter((acc) => acc.remaining > 0)
          .map((fee) => ({
            feeType: fee.feeFineType || '-',
            amount: fee.remaining || 0,
            createdDate: fee.metadata?.createdDate
              ? new Date(fee.metadata.createdDate).toLocaleDateString('ar-SA')
              : '-',
            status: fee.paymentStatus?.name || fee.status?.name || '-',
            relatedItem: fee.title || '-',
          })),
      };

      const translations = {
        reportTitle: intl.formatMessage({
          id: 'ui-users.openTransactions.report.reportTitle',
        }),
        sheetName: intl.formatMessage({
          id: 'ui-users.openTransactions.report.sheetName',
        }),
        userName: intl.formatMessage({
          id: 'ui-users.openTransactions.report.userName',
        }),
        barcode: intl.formatMessage({
          id: 'ui-users.openTransactions.report.barcode',
        }),
        patronGroup: intl.formatMessage({
          id: 'ui-users.openTransactions.report.patronGroup',
        }),
        reportDate: intl.formatMessage({
          id: 'ui-users.openTransactions.report.reportDate',
        }),
        summary: intl.formatMessage({
          id: 'ui-users.openTransactions.report.summary',
        }),
        totalOpenLoans: intl.formatMessage({
          id: 'ui-users.openTransactions.report.totalOpenLoans',
        }),
        totalOpenRequests: intl.formatMessage({
          id: 'ui-users.openTransactions.report.totalOpenRequests',
        }),
        totalOutstandingFees: intl.formatMessage({
          id: 'ui-users.openTransactions.report.totalOutstandingFees',
        }),
        totalFeeAmount: intl.formatMessage({
          id: 'ui-users.openTransactions.report.totalFeeAmount',
        }),
        currency: intl.formatMessage({
          id: 'ui-users.openTransactions.report.currency',
        }),
        openLoans: intl.formatMessage({
          id: 'ui-users.openTransactions.report.openLoans',
        }),
        itemTitle: intl.formatMessage({
          id: 'ui-users.openTransactions.report.itemTitle',
        }),
        itemBarcode: intl.formatMessage({
          id: 'ui-users.openTransactions.report.itemBarcode',
        }),
        loanDate: intl.formatMessage({
          id: 'ui-users.openTransactions.report.loanDate',
        }),
        dueDate: intl.formatMessage({
          id: 'ui-users.openTransactions.report.dueDate',
        }),
        overdueDays: intl.formatMessage({
          id: 'ui-users.openTransactions.report.overdueDays',
        }),
        status: intl.formatMessage({
          id: 'ui-users.openTransactions.report.status',
        }),
        openRequests: intl.formatMessage({
          id: 'ui-users.openTransactions.report.openRequests',
        }),
        requestType: intl.formatMessage({
          id: 'ui-users.openTransactions.report.requestType',
        }),
        requestDate: intl.formatMessage({
          id: 'ui-users.openTransactions.report.requestDate',
        }),
        requestStatus: intl.formatMessage({
          id: 'ui-users.openTransactions.report.requestStatus',
        }),
        servicePoint: intl.formatMessage({
          id: 'ui-users.openTransactions.report.servicePoint',
        }),
        outstandingFees: intl.formatMessage({
          id: 'ui-users.openTransactions.report.outstandingFees',
        }),
        feeType: intl.formatMessage({
          id: 'ui-users.openTransactions.report.feeType',
        }),
        amount: intl.formatMessage({
          id: 'ui-users.openTransactions.report.amount',
        }),
        createdDate: intl.formatMessage({
          id: 'ui-users.openTransactions.report.createdDate',
        }),
        feeStatus: intl.formatMessage({
          id: 'ui-users.openTransactions.report.feeStatus',
        }),
        relatedItem: intl.formatMessage({
          id: 'ui-users.openTransactions.report.relatedItem',
        }),
        footerNote: intl.formatMessage({
          id: 'ui-users.openTransactions.report.footerNote',
        }),
      };

      let result;
      if (format === 'excel') {
        result = await exportOpenTransactionsToExcel(
          data,
          reportName,
          translations,
        );
      } else {
        result = await exportOpenTransactionsToPdf(
          data,
          reportName,
          translations,
        );
      }

      if (result.success) {
        this.context.sendCallout({
          message: (
            <FormattedMessage id="ui-users.openTransactions.report.success" />
          ),
        });
      } else {
        this.context.sendCallout({
          type: 'error',
          message: (
            <FormattedMessage id="ui-users.openTransactions.report.error" />
          ),
        });
      }
    } catch (error) {
      console.error('Open transactions report error:', error);
      this.context.sendCallout({
        type: 'error',
        message: (
          <FormattedMessage id="ui-users.openTransactions.report.error" />
        ),
      });
    } finally {
      this.setState({ openTransactionsReportInProgress: false });
    }
  };
  /** kware end edit */

  getActionMenu =
    (barcode) =>
    ({ onToggle }) => {
      const {
        okapi: {
          currentUser: { servicePoints },
        },
        resources,
        stripes,
      } = this.props;
      const user = this.getUser();
      const patronGroup = this.getPatronGroup(user);
      const feeFineActions = get(resources, ['feefineactions', 'records'], []);
      const accounts = get(resources, ['accounts', 'records'], []);
      const loans = get(resources, ['loanRecords', 'records'], []);
      const isShadowUser = user?.type === USER_TYPES.SHADOW;
      const isVirtualPatron = isDcbUser(user);

      const feesFinesReportData = {
        user,
        patronGroup: patronGroup.group,
        servicePoints,
        feeFineActions,
        accounts,
        loans,
      };

      const showActionMenu =
        stripes.hasPerm('ui-users.edit') ||
        stripes.hasPerm('ui-users.patron_blocks') ||
        stripes.hasPerm('ui-users.feesfines.actions.all') ||
        stripes.hasPerm('ui-requests.all') ||
        stripes.hasPerm('ui-users.delete,ui-users.opentransactions') ||
        stripes.hasPerm('ui-users.profile-pictures.all');

      if (showActionMenu && !isVirtualPatron) {
        return (
          <>
            {!isShadowUser && (
              <IfInterface name="feesfines">
                <RequestFeeFineBlockButtons
                  barcode={barcode}
                  onToggle={onToggle}
                  userId={this.props.match.params.id}
                  disabled={isShadowUser}
                />
              </IfInterface>
            )}
            <ActionMenuEditButton
              id={this.props.match.params.id}
              suppressList={this.props.resources.suppressEdit}
              onToggle={onToggle}
              goToEdit={this.goToEdit}
              editButton={this.editButton}
            />
            {!isShadowUser && <LostItemsLink />}
            <IfInterface name="feesfines">
              <ExportFeesFinesReportButton
                feesFinesReportData={feesFinesReportData}
                onToggle={onToggle}
                callout={this.callout}
              />
            </IfInterface>
            <ActionMenuOpenTransactionsButton
              handleOpenTransactionsReportClick={
                this.handleOpenTransactionsReportClick
              }
              onToggle={onToggle}
            />
            {/* kware start edit - Add change password button for staff users */}
            {user?.type === USER_TYPES.STAFF && (
              <ActionMenuChangePasswordButton
                handleChangePasswordClick={this.handleChangePasswordClick}
                onToggle={onToggle}
              />
            )}
            {/* kware end edit */}
            {/* kware start edit - Add clearance button to action menu */}
            <ActionMenuClearanceButton
              handleClearanceClick={
                user?.customFields?.clearancestatus === 'opt_0'
                  ? this.handleUndoClearanceClick
                  : this.handleClearanceClick
              }
              id={this.props.match.params.id}
              onToggle={onToggle}
              suppressList={this.props.resources.suppressEdit}
              isCleared={user?.customFields?.clearancestatus === 'opt_0'}
            />
            {/* kware end edit */}
            <ActionMenuDeleteButton
              handleDeleteClick={this.handleDeleteClick}
              id={this.props.match.params.id}
              onToggle={onToggle}
              suppressList={this.props.resources.suppressEdit}
            />
          </>
        );
      } else {
        return null;
      }
    };

  checkScope = () => true;

  goToEdit = () => {
    const {
      history,
      location: { search },
      match: { params },
    } = this.props;
    history.push(`/users/${params.id}/edit${search}`);
  };

  shortcuts = [
    {
      name: 'edit',
      handler: this.goToEdit,
    },
    {
      name: 'expandAllSections',
      handler: this.expandAllSections,
    },
    {
      name: 'collapseAllSections',
      handler: this.collapseAllSections,
    },
  ];

  getAddressesList(addresses, addressTypes) {
    const addressTypesById = keyBy(addressTypes, 'id');

    return addresses.map((address) => {
      const addressTypeOption = addressTypesById[address.addressType];
      const addressType = get(addressTypeOption, ['addressType']);

      return { ...address, addressType };
    });
  }

  userNotFound = () => {
    return this.props.resources?.selUser?.failed?.httpStatus === 404;
  };

  loadPatronBlocks() {
    const {
      mutator: { hasManualPatronBlocks, hasAutomatedPatronBlocks },
      stripes,
    } = this.props;

    const manualPatronBlocksResolver =
      stripes.hasInterface('feesfines') &&
      stripes.hasPerm('manualblocks.collection.get')
        ? hasManualPatronBlocks.GET().catch(() => [])
        : Promise.resolve([]);
    const automatedPatronBlocksResolver =
      stripes.hasInterface('automated-patron-blocks') &&
      stripes.hasPerm('automated-patron-blocks.collection.get')
        ? hasAutomatedPatronBlocks.GET().catch(() => [])
        : Promise.resolve([]);

    Promise.all([
      manualPatronBlocksResolver,
      automatedPatronBlocksResolver,
    ]).then(([manualPatronBlocks, automatedPatronBlocks]) => {
      const { sections } = this.state;

      if (!this._isMounted) return;

      let patronBlocks = concat(
        manualPatronBlocks,
        automatedPatronBlocks,
      ).filter((patronBlock) => {
        return moment(patronBlock.expirationDate)
          .endOf('day')
          .isSameOrAfter(moment().endOf('day'));
      });

      patronBlocks = orderBy(patronBlocks, ['metadata.createdDate'], ['desc']);

      this.setState({
        patronBlocks,
      });

      if (!sections.patronBlocksSection && patronBlocks.length) {
        this.handleSectionToggle({ id: 'patronBlocksSection' });
      }
    });
  }

  render() {
    const { resources, stripes, location, match, onClose, paneWidth, intl } =
      this.props;

    const { sections, helperApp, patronBlocks } = this.state;

    const user = this.getUser();
    const fullNameOfUser = getFullName(user, intl);
    const userId = match.params.id;

    const addressTypes = (resources.addressTypes || {}).records || [];
    const addresses = getFormAddressList(get(user, 'personal.addresses', []));
    const addressesList = this.getAddressesList(addresses, addressTypes);
    const settings = (resources.settings || {}).records || [];
    const sponsors = this.props.getSponsors();
    const proxies = this.props.getProxies();
    const servicePoints = this.props.getUserServicePoints();
    const preferredServicePoint = this.props.getPreferredServicePoint();
    const hasPatronBlocks = !!patronBlocks.length;
    const hasPatronBlocksPermissions =
      stripes.hasPerm('automated-patron-blocks.collection.get') ||
      stripes.hasPerm('manualblocks.collection.get');
    const patronGroup = this.getPatronGroup(user);
    const requestPreferences = get(
      resources,
      'requestPreferences.records.[0].requestPreferences[0]',
      {},
    );
    const allServicePoints = get(resources, 'servicePoints.records', [{}]);
    const defaultServicePointName = get(
      allServicePoints.find(
        (servicePoint) =>
          servicePoint.id === requestPreferences.defaultServicePointId,
      ),
      'name',
      '',
    );
    const defaultDeliveryAddressTypeName = get(
      addressTypes.find(
        (addressType) =>
          addressType.id === requestPreferences.defaultDeliveryAddressTypeId,
      ),
      'addressType',
      '',
    );
    const customFields = user?.customFields || {};
    const departments = resources?.departments?.records || [];
    const userDepartments = (user?.departments || []).map(
      (departmentId) => departments.find(({ id }) => id === departmentId)?.name,
    );
    const accounts = resources?.accounts;
    const isAffiliationsVisible = isAffiliationsEnabled(user);

    const isShadowUser = user?.type === USER_TYPES.SHADOW;
    const isVirtualPatron = isDcbUser(user);
    const showPatronBlocksSection = hasPatronBlocksPermissions && !isShadowUser;

    if (this.userNotFound()) {
      return (
        <ErrorPane
          id="pane-user-not-found"
          defaultWidth={paneWidth}
          paneTitle={<FormattedMessage id="ui-users.information.userDetails" />}
          dismissible
          onClose={this.onClose}
        >
          <FormattedMessage id="ui-users.errors.userNotFound" />
        </ErrorPane>
      );
    }

    if (!user) {
      return (
        <LoadingPane
          id="pane-userdetails"
          defaultWidth={paneWidth}
          paneTitle={<FormattedMessage id="ui-users.information.userDetails" />}
          dismissible
          onClose={onClose}
        />
      );
    } else {
      return (
        <HasCommand
          commands={this.shortcuts}
          isWithinScope={this.checkScope}
          scope={document.body}
        >
          <>
            <Pane
              data-test-instance-details
              id="pane-userdetails"
              appIcon={<AppIcon app="users" appIconKey="users" />}
              defaultWidth={paneWidth}
              paneTitle={
                <span data-test-header-title>{getFullName(user, intl)}</span>
              }
              actionMenu={this.getActionMenu(get(user, 'barcode', ''))}
              lastMenu={this.renderDetailsLastMenu(user)}
              dismissible
              onClose={this.onClose}
            >
              <TitleManager record={getFullName(user, intl)} />
              <Headline size="xx-large" tag="h2">
                {getFullName(user, intl)}
              </Headline>
              <Row>
                <Col xs={10}>
                  {hasPatronBlocks ? <PatronBlockMessage /> : ''}
                  {/* kware start edit - Add clearance banner */}
                  {user?.customFields?.clearancestatus === 'opt_0' &&
                    user?.customFields?.clearancedate && (
                      <ClearanceBanner
                        clearanceDate={user.customFields.clearancedate}
                        libraryName={this.getLibraryName(
                          user.customFields.clearancelibrary,
                        )}
                      />
                    )}
                  {/* kware end edit */}
                </Col>
                <Col xs={2}>
                  <ExpandAllButton
                    accordionStatus={sections}
                    onToggle={this.handleExpandAll}
                  />
                </Col>
              </Row>
              <AccordionSet>
                <UserInfo
                  accordionId="userInformationSection"
                  user={user}
                  patronGroup={patronGroup}
                  settings={settings}
                  stripes={stripes}
                  expanded={sections.userInformationSection}
                  onToggle={this.handleSectionToggle}
                />

                <IfConsortium>
                  <IfConsortiumPermission perm="consortia.user-tenants.collection.get">
                    {isAffiliationsVisible && (
                      <UserAffiliations
                        accordionId="affiliationsSection"
                        expanded={sections.affiliationsSection}
                        onToggle={this.handleSectionToggle}
                        userId={user?.id}
                        userName={user?.username}
                      />
                    )}
                  </IfConsortiumPermission>
                </IfConsortium>

                <IfInterface name="feesfines">
                  {showPatronBlocksSection && (
                    <PatronBlock
                      accordionId="patronBlocksSection"
                      patronBlocks={patronBlocks}
                      expanded={sections.patronBlocksSection}
                      onToggle={this.handleSectionToggle}
                      onClickViewPatronBlock={this.onClickViewPatronBlock}
                      {...this.props}
                    />
                  )}
                </IfInterface>
                <ExtendedInfo
                  accordionId="extendedInfoSection"
                  user={user}
                  expanded={sections.extendedInfoSection}
                  requestPreferences={requestPreferences}
                  defaultServicePointName={defaultServicePointName}
                  defaultDeliveryAddressTypeName={
                    defaultDeliveryAddressTypeName
                  }
                  onToggle={this.handleSectionToggle}
                  departments={userDepartments}
                />
                <ContactInfo
                  accordionId="contactInfoSection"
                  stripes={stripes}
                  user={user}
                  addresses={addressesList}
                  addressTypes={addressTypes}
                  expanded={sections.contactInfoSection}
                  onToggle={this.handleSectionToggle}
                />
                <ClearanceInfo
                  accordionId="clearanceSection"
                  user={user}
                  expanded={sections.clearanceSection}
                  onToggle={this.handleSectionToggle}
                  getLibraryName={this.getLibraryName}
                  getUserNameById={this.getUserNameById}
                />
                <DefaultLibraryInfo
                  accordionId="defaultLibrarySection"
                  user={user}
                  expanded={sections.defaultLibrarySection}
                  onToggle={this.handleSectionToggle}
                  getLibraryName={this.getLibraryName}
                />
                {/* kware start edit - Hide ViewCustomFieldsRecord to avoid duplicate clearance sections
                    We now use ClearanceInfo component above which provides better control and fixes
                    the library name display issue. The ViewCustomFieldsRecord is kept commented
                    for potential future use of other custom fields.
                <ViewCustomFieldsRecord
                  accordionId="customFields"
                  onToggle={this.handleSectionToggle}
                  expanded={sections.customFields}
                  backendModuleName="users"
                  entityType="user"
                  customFieldsValues={customFields}
                  customFieldsLabel={
                    <FormattedMessage id="ui-users.custom.customFields" />
                  }
                  noCustomFieldsFoundLabel={
                    <FormattedMessage id="ui-users.custom.noCustomFieldsFound" />
                  }
                />
                kware end edit */}
                {!isShadowUser && (
                  <>
                    <IfPermission perm="proxiesfor.collection.get">
                      <ProxyPermissions
                        user={user}
                        accordionId="proxySection"
                        onToggle={this.handleSectionToggle}
                        proxies={proxies}
                        sponsors={sponsors}
                        expanded={sections.proxySection}
                        {...this.props}
                      />
                    </IfPermission>

                    <IfInterface name="feesfines">
                      <IfPermission perm="ui-users.feesfines.view">
                        <UserAccounts
                          expanded={sections.accountsSection}
                          onToggle={this.handleSectionToggle}
                          accordionId="accountsSection"
                          location={location}
                          accounts={accounts}
                          match={match}
                          {...this.props}
                        />
                      </IfPermission>
                    </IfInterface>

                    <IfPermission perm="ui-users.loans.view">
                      <IfInterface name="loan-policy-storage">
                        {/* Check without version, so can support either of multiple versions.
                    Replace with specific check when facility for providing
                    multiple versions is available */}
                        <IfInterface name="circulation">
                          <UserLoans
                            onClickViewLoanActionsHistory={
                              this.onClickViewLoanActionsHistory
                            }
                            onClickViewOpenLoans={this.onClickViewOpenLoans}
                            onClickViewClosedLoans={this.onClickViewClosedLoans}
                            expanded={sections.loansSection}
                            onToggle={this.handleSectionToggle}
                            accordionId="loansSection"
                            {...this.props}
                          />
                        </IfInterface>
                      </IfInterface>
                    </IfPermission>

                    <IfPermission perm="ui-users.requests.all">
                      <IfInterface
                        name="request-storage"
                        version="2.5 3.0 4.0 5.0 6.0"
                      >
                        <IfInterface name="circulation">
                          <UserRequests
                            expanded={sections.requestsSection}
                            onToggle={this.handleSectionToggle}
                            accordionId="requestsSection"
                            user={user}
                            {...this.props}
                          />
                        </IfInterface>
                      </IfInterface>
                    </IfPermission>
                  </>
                )}

                <IfPermission perm="perms.users.get">
                  <IfInterface name="permissions" version="5.0">
                    <UserPermissions
                      expanded={sections.permissionsSection}
                      onToggle={this.handleSectionToggle}
                      accordionId="permissionsSection"
                      user={this.getUser()}
                      {...this.props}
                    />
                  </IfInterface>
                </IfPermission>

                <IfPermission perm="inventory-storage.service-points.collection.get,inventory-storage.service-points-users.collection.get">
                  <IfInterface name="service-points-users" version="1.0">
                    <UserServicePoints
                      expanded={sections.servicePointsSection}
                      onToggle={this.handleSectionToggle}
                      accordionId="servicePointsSection"
                      servicePoints={servicePoints}
                      preferredServicePoint={preferredServicePoint}
                      {...this.props}
                    />
                  </IfInterface>
                </IfPermission>
                <IfInterface name="notes">
                  <IfPermission perm="ui-notes.item.view">
                    <NotesSmartAccordion
                      domainName="users"
                      entityId={match.params.id}
                      entityName={getFullName(user, intl)}
                      open={this.state.sections.notesAccordion}
                      onToggle={this.handleSectionToggle}
                      id="notesAccordion"
                      entityType="user"
                      pathToNoteCreate="/users/notes/new"
                      pathToNoteDetails="/users/notes"
                      hideAssignButton
                      hideNewButton={isVirtualPatron}
                    />
                  </IfPermission>
                </IfInterface>
              </AccordionSet>
            </Pane>
            {helperApp && (
              <HelperApp appName={helperApp} onClose={this.closeHelperApp} />
            )}
            <Callout
              ref={(ref) => {
                this.callout = ref;
              }}
            />
            <IfInterface name="notes">
              <IfPermission perm="ui-notes.item.view">
                <NotePopupModal
                  id="user-popup-note-modal"
                  domainName="users"
                  entityType="user"
                  popUpPropertyName="popUpOnUser"
                  entityId={user?.id}
                  label={intl.formatMessage({
                    id: 'ui-users.notes.popupModal.label',
                  })}
                />
              </IfPermission>
            </IfInterface>
            {this.state.showDeleteUserModal && (
              <DeleteUserModal
                onCloseModal={this.doCloseTransactionDeleteModal}
                username={fullNameOfUser}
                userId={userId}
                deleteUser={this.handleDeleteUser}
              />
            )}
            {this.state.showOpenTransactionModal && (
              <OpenTransactionModal
                onCloseModal={this.doCloseTransactionDeleteModal}
                openTransactions={this.state.openTransactions}
                username={fullNameOfUser}
              />
            )}
            {/* kware start edit - Add clearance modal to render */}
            {this.state.showClearanceModal && (
              <ClearanceModal
                onCloseModal={this.handleCloseClearanceModal}
                onConfirm={this.handleClearanceConfirm}
                username={fullNameOfUser}
                isLoading={this.state.clearanceLoading}
                libraries={get(resources, 'libraries.records', [])}
                currentUser={this.props.okapi.currentUser}
                locations={get(resources, 'locations.records', [])}
              />
            )}
            {this.state.showClearanceBlockedModal && (
              <ClearanceBlockedModal
                onCloseModal={this.handleCloseClearanceBlockedModal}
                openTransactions={this.state.openTransactions}
                username={fullNameOfUser}
              />
            )}
            {/* kware start edit - Add undo clearance modal */}
            {this.state.showUndoClearanceModal && (
              <ClearanceModal
                onCloseModal={this.handleCloseUndoClearanceModal}
                onConfirm={this.handleUndoClearanceConfirm}
                username={fullNameOfUser}
                isLoading={this.state.clearanceLoading}
                isUndo
              />
            )}
            {/* kware end edit */}
            {this.state.showOpenTransactionsReportModal && (
              <OpenTransactionsReportModal
                open={this.state.showOpenTransactionsReportModal}
                onClose={this.handleCloseOpenTransactionsReportModal}
                onExport={this.handleOpenTransactionsReportExport}
                isLoading={this.state.openTransactionsReportInProgress}
                userName={fullNameOfUser}
              />
            )}
            {/* kware start edit - Add change password modal for staff users */}
            {this.state.showChangePasswordModal && (
              <ChangePasswordModal
                isOpen={this.state.showChangePasswordModal}
                onClose={this.handleCloseChangePasswordModal}
                onSubmit={this.handleChangePasswordSubmit}
                userName={fullNameOfUser}
                isLoading={this.state.changePasswordLoading}
              />
            )}
            {/* kware end edit */}
            {/* kware end edit */}
          </>
        </HasCommand>
      );
    }
  }
}

export default withOkapiKy(injectIntl(UserDetail));
